import { useState } from "react";
import {
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@tanstack/react-query";
import DashboardLayout from "layout";
import DataTable, { DataColumn } from "components/DataTable";
import StatusBadge from "components/StatusBadge";
import useDebounce from "hooks/useDebounce";
import useResettableState from "hooks/useResettableState";
import {
  ESIM_STATUSES,
  ESIM_STATUS_TONES,
  EsimOrder,
  getEsimOrders,
  orderCustomerName,
  orderDate,
  orderIccid,
  orderPackage,
} from "services/esim";
import { formatNaira } from "services/finance";
import { formatDisplayDate } from "utils/helpers";
import AiraloBalance from "./AiraloBalance";
import EsimOrderPanel from "./EsimOrderPanel";

const LIMIT = 20;

const columns: DataColumn<EsimOrder>[] = [
  { label: "Order ref", render: (o) => <b>{o.order_ref}</b> },
  { label: "Kind", render: (o) => (o.kind === "topup" ? "Top-up" : "eSIM") },
  {
    label: "Customer",
    render: (o) => (
      <Box minWidth={0}>
        <Typography fontSize={13} fontWeight={500} color="#101828" noWrap>
          {orderCustomerName(o) || "—"}
        </Typography>
        <Typography fontSize={12} color="#667085" noWrap>
          {o.email || ""}
        </Typography>
      </Box>
    ),
  },
  {
    label: "Plan",
    render: (o) => {
      const pkg = orderPackage(o);
      return (
        <Box display="flex" alignItems="center" gap="8px">
          {pkg.destination_image && (
            <Box
              component="img"
              src={pkg.destination_image}
              alt=""
              width={24}
              height={18}
              borderRadius="3px"
              sx={{ objectFit: "cover" }}
            />
          )}
          <Box>
            <Typography fontSize={13} color="#101828">
              {pkg.destination_title || "—"}
            </Typography>
            <Typography fontSize={12} color="#667085">
              {pkg.title || ""}
            </Typography>
          </Box>
        </Box>
      );
    },
  },
  { label: "Total", render: (o) => formatNaira(o.total_price) },
  { label: "Status", render: (o) => <StatusBadge status={o.status} tones={ESIM_STATUS_TONES} /> },
  { label: "Created", render: (o) => formatDisplayDate(orderDate(o)) },
  { label: "ICCID", render: (o) => orderIccid(o) || "—" },
];

const EsimOrders = () => {
  const [selected, setSelected] = useState<EsimOrder | null>(null);
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 400);
  const [page, setPage] = useResettableState(1, [status, kind, debouncedSearch].join("|"));

  const params = { status, kind, search: debouncedSearch, page, limit: LIMIT };
  const { data, isLoading } = useQuery({
    queryKey: ["esimOrders", params],
    queryFn: () => getEsimOrders(params),
    placeholderData: (prev) => prev,
  });

  const orders = data?.items || [];
  const totalPages = Math.max(Math.ceil((data?.total || 0) / LIMIT), 1);

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ width: "100%", p: { xs: 0, sm: 2 } }}>
        <Box
          mb={3}
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap="12px"
        >
          <Box>
            <Typography variant="h5" fontWeight={600} color="#101828">
              eSIM Orders
            </Typography>
            <Typography variant="body2" color="#475467">
              eSIM purchases and top-ups. Failed orders need an admin to retry or refund them.
            </Typography>
          </Box>
          <AiraloBalance />
        </Box>

        <Box display="flex" flexWrap="wrap" gap="12px" mb="16px" alignItems="center">
          <TextField
            size="small"
            placeholder="Search order ref, email or ICCID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {ESIM_STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                {s.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Kind"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="esim">eSIM</MenuItem>
            <MenuItem value="topup">Top-up</MenuItem>
          </TextField>
          <Chip
            label="Failed only"
            color={status === "failed" ? "error" : "default"}
            variant={status === "failed" ? "filled" : "outlined"}
            onClick={() => setStatus(status === "failed" ? "" : "failed")}
          />
        </Box>

        <DataTable
          columns={columns}
          rows={orders}
          loading={isLoading}
          emptyText="No eSIM orders found"
          rowKey={(o) => o.order_ref}
          onRowClick={setSelected}
        />

        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      </Box>

      {selected && <EsimOrderPanel order={selected} onClose={() => setSelected(null)} />}
    </DashboardLayout>
  );
};

export default EsimOrders;
